import React, { createContext, useContext, useState } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const currencies = {
  INR: {
    symbol: '₹',
    name: 'Indian Rupee',
    code: 'INR',
    rate: 1 // Base currency
  },
  USD: {
    symbol: '$',
    name: 'US Dollar',
    code: 'USD',
    rate: 0.012 // 1 INR = 0.012 USD (approximate)
  },
  EUR: {
    symbol: '€',
    name: 'Euro',
    code: 'EUR',
    rate: 0.011 // 1 INR = 0.011 EUR (approximate)
  },
  GBP: {
    symbol: '£',
    name: 'British Pound',
    code: 'GBP',
    rate: 0.0095 // 1 INR = 0.0095 GBP (approximate)
  },
  AUD: {
    symbol: 'A$',
    name: 'Australian Dollar',
    code: 'AUD',
    rate: 0.018 // 1 INR = 0.018 AUD (approximate)
  },
  CAD: {
    symbol: 'C$',
    name: 'Canadian Dollar',
    code: 'CAD',
    rate: 0.016 // 1 INR = 0.016 CAD (approximate)
  }
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('INR');

  const formatPrice = (priceInINR, showCurrency = true) => {
    const currency = currencies[selectedCurrency];
    const convertedPrice = priceInINR * currency.rate;
    
    let formattedPrice;
    if (selectedCurrency === 'INR') {
      // Indian number formatting (lakhs, crores)
      if (convertedPrice >= 10000000) {
        formattedPrice = `${(convertedPrice / 10000000).toFixed(1)}Cr`;
      } else if (convertedPrice >= 100000) {
        formattedPrice = `${(convertedPrice / 100000).toFixed(1)}L`;
      } else if (convertedPrice >= 1000) {
        formattedPrice = `${(convertedPrice / 1000).toFixed(0)}K`;
      } else {
        formattedPrice = Math.round(convertedPrice).toLocaleString('en-IN');
      }
    } else {
      // International formatting
      if (convertedPrice >= 1000000) {
        formattedPrice = `${(convertedPrice / 1000000).toFixed(1)}M`;
      } else if (convertedPrice >= 1000) {
        formattedPrice = `${(convertedPrice / 1000).toFixed(0)}K`;
      } else {
        formattedPrice = Math.round(convertedPrice).toLocaleString();
      }
    }

    return showCurrency ? `${currency.symbol}${formattedPrice}` : formattedPrice;
  };

  const getCurrencySymbol = () => {
    return currencies[selectedCurrency].symbol;
  };

  const getCurrencyName = () => {
    return currencies[selectedCurrency].name;
  };

  const changeCurrency = (currencyCode) => {
    if (currencies[currencyCode]) {
      setSelectedCurrency(currencyCode);
    }
  };

  const value = {
    selectedCurrency,
    currencies,
    formatPrice,
    getCurrencySymbol,
    getCurrencyName,
    changeCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};